import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useT } from "../i18n/I18nContext";
import { toast } from "sonner";
import { UserCog, UserPlus, Trash2 } from "lucide-react";

function randomPwd() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out + "!";
}

export default function AdminUsersPage() {
  const t = useT();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        name: editing.name,
        phone: editing.phone,
        role: editing.role,
        active: editing.active,
        credential_delivery: editing.credential_delivery || "personal",
      };
      if (editing.new_password) payload.password = editing.new_password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success(t("admin.users.toast.updated"));
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createUser = async () => {
    if (!creating.username || creating.username.length < 3 || !creating.password || creating.password.length < 6 || !creating.name) {
      toast.error(t("interest.error.required"));
      return;
    }
    try {
      await api.post("/users", {
        username: creating.username.trim().toLowerCase(),
        password: creating.password,
        name: creating.name.trim(),
        role: creating.role,
        phone: creating.phone || null,
        email: creating.email?.trim() || null,
        credential_delivery: creating.credential_delivery,
      });
      toast.success(t("admin.leads.created_user"));
      setCreating(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(t("admin.users.confirm_delete"))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("admin.users.toast.deleted"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <UserCog className="w-6 h-6 text-[#0F4C3A]" />
          <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#0F4C3A]" data-testid="admin-users-title">{t("admin.users.title")}</h1>
        </div>
        <button
          onClick={() => setCreating({ username: "", password: randomPwd(), name: "", role: "participant", phone: "", email: "", credential_delivery: "personal" })}
          data-testid="admin-users-new-btn"
          className="nv-btn-primary px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 text-sm"
        >
          <UserPlus className="w-4 h-4" /> {t("admin.users.new")}
        </button>
      </div>

      {/* Mobile cards (< md) */}
      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.id} className="nv-border bg-white rounded-md p-4" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-[#0F4C3A] truncate">{u.name}</div>
                <div className="text-sm text-[#475569] truncate">{u.username || u.email || "—"}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider bg-[#F8FAF9] px-2 py-1 rounded">{t(`role.${u.role}`)}</span>
                  <span className="text-xs">{u.active ? "✓" : "✕"}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setEditing({ ...u, new_password: "" })} data-testid={`edit-user-mobile-${u.id}`} className="px-3 py-1.5 text-xs border border-[#E2E8E4] rounded hover:bg-[#F8FAF9] whitespace-nowrap">
                  {t("admin.users.edit")}
                </button>
                <button onClick={() => removeUser(u)} data-testid={`delete-user-mobile-${u.id}`} className="p-1.5 text-[#DC2626] hover:bg-[#FEE2E2] rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table (>= md) */}
      <div className="hidden md:block nv-border bg-white rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8FAF9] border-b border-[#E2E8E4]">
            <tr>
              <th className="nv-label p-3 text-left">{t("admin.users.col.name")}</th>
              <th className="nv-label p-3 text-left">{t("admin.users.col.username")}</th>
              <th className="nv-label p-3 text-left">{t("admin.users.col.email")}</th>
              <th className="nv-label p-3 text-left">{t("admin.users.col.role")}</th>
              <th className="nv-label p-3 text-left">{t("admin.users.col.active")}</th>
              <th className="nv-label p-3 text-right">{t("admin.users.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#E2E8E4] hover:bg-[#F8FAF9]" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-medium text-[#0F4C3A]">{u.name}</td>
                <td className="p-3 text-sm text-[#0F4C3A] font-mono-nv">{u.username || "—"}</td>
                <td className="p-3 text-sm text-[#475569]">{u.email || "—"}</td>
                <td className="p-3"><span className="text-xs uppercase tracking-wider bg-[#F8FAF9] px-2 py-1 rounded">{t(`role.${u.role}`)}</span></td>
                <td className="p-3 text-sm">{u.active ? "✓" : "✕"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing({ ...u, new_password: "" })} data-testid={`edit-user-${u.id}`} className="px-3 py-1.5 text-xs border border-[#E2E8E4] rounded hover:bg-white">
                    {t("admin.users.edit")}
                  </button>
                  <button onClick={() => removeUser(u)} data-testid={`delete-user-${u.id}`} className="ml-2 p-1.5 text-[#DC2626] hover:bg-[#FEE2E2] rounded align-middle">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl text-[#0F4C3A]">{t("admin.users.modal.title")}</h3>
            <div className="mt-5 space-y-4">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" placeholder={t("admin.users.modal.name")} className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md" />
              <input value={editing.username || ""} disabled placeholder={t("admin.users.col.username")} className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md bg-[#F8FAF9] font-mono-nv" />
              <input value={editing.email || ""} disabled placeholder={t("admin.users.col.email")} className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md bg-[#F8FAF9]" />
              <input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder={t("admin.users.modal.phone")} className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md" />
              <div>
                <label className="nv-label block mb-1">{t("admin.users.delivery")}</label>
                <select value={editing.credential_delivery || "personal"} onChange={(e) => setEditing({ ...editing, credential_delivery: e.target.value })} data-testid="edit-user-delivery" className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md">
                  <option value="email">{t("delivery.email")}</option>
                  <option value="phone">{t("delivery.phone")}</option>
                  <option value="personal">{t("delivery.personal")}</option>
                </select>
              </div>
              <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="edit-user-role" className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md">
                <option value="participant">{t("role.participant")}</option>
                <option value="admin">{t("role.admin")}</option>
                <option value="controller">{t("role.controller")}</option>
              </select>
              <input
                type="password"
                value={editing.new_password}
                onChange={(e) => setEditing({ ...editing, new_password: e.target.value })}
                data-testid="edit-user-password"
                placeholder={t("admin.users.modal.password")}
                className="w-full px-3 py-2.5 border border-[#E2E8E4] rounded-md"
              />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                {t("admin.users.modal.active")}
              </label>
            </div>
            <div className="flex gap-3 pt-5">
              <button onClick={save} data-testid="save-user-button" className="flex-1 nv-btn-primary py-2.5 rounded-md">{t("admin.users.modal.save")}</button>
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 border border-[#E2E8E4] rounded-md">{t("common.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCreating(null)}>
          <div className="bg-white rounded-md p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl text-[#0F4C3A]">{t("admin.users.new.title")}</h3>
            <div className="mt-5 space-y-3">
              <input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="new-user-name" placeholder={t("admin.leads.modal.name")} className="nv-input" />
              <input value={creating.username} onChange={(e) => setCreating({ ...creating, username: e.target.value.toLowerCase() })} data-testid="new-user-username" placeholder={t("admin.users.new.username")} className="nv-input font-mono-nv" />
              <input value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} data-testid="new-user-password" placeholder={t("admin.users.new.password")} className="nv-input font-mono-nv" />
              <input value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} placeholder={t("admin.users.new.email")} className="nv-input" />
              <input value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} placeholder={t("admin.leads.modal.phone")} className="nv-input" />
              <div>
                <label className="nv-label block mb-1">{t("admin.users.delivery")}</label>
                <select value={creating.credential_delivery} onChange={(e) => setCreating({ ...creating, credential_delivery: e.target.value })} data-testid="new-user-delivery" className="nv-input">
                  <option value="email">{t("delivery.email")}</option>
                  <option value="phone">{t("delivery.phone")}</option>
                  <option value="personal">{t("delivery.personal")}</option>
                </select>
              </div>
              <select value={creating.role} onChange={(e) => setCreating({ ...creating, role: e.target.value })} className="nv-input">
                <option value="participant">{t("role.participant")}</option>
                <option value="admin">{t("role.admin")}</option>
                <option value="controller">{t("role.controller")}</option>
              </select>
            </div>
            <div className="flex gap-3 pt-5">
              <button onClick={createUser} data-testid="create-user-submit" className="flex-1 nv-btn-primary py-2.5 rounded-md">{t("admin.leads.modal.create")}</button>
              <button onClick={() => setCreating(null)} className="flex-1 py-2.5 border border-[#E2E8E4] rounded-md">{t("common.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
